import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import DemoModal from '../components/DemoModal';
import { ArrowRight } from 'lucide-react';

const SubPageTemplate = ({ title, description, features = [] }) => {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="pt-20 sm:pt-28 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-medium text-black/95 mb-4 sm:mb-6">
              {title}
            </h1>
            <p className="text-base sm:text-lg text-black/70 max-w-2xl mx-auto mb-6 sm:mb-8">
              {description}
            </p>
            <Button
              size="lg"
              onClick={() => setDemoOpen(true)}
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105"
            >
              Get started
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-14 md:py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <Card className="h-full bg-[#E8E4DC] border-[#F4C2D4]/30 hover:bg-[#FDF7F9] hover:border-[#E8A0B8]/50 hover:-translate-y-1 transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="text-black/90 text-lg font-sans">
                      {feature.title}
                    </CardTitle>
                    <CardDescription className="text-black/70 text-sm">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-gradient-to-br from-[#E8EDE7] to-[#D4DDD1]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium text-black/95 mb-4 sm:mb-6">
              Ready to get started?
            </h2>
            <p className="text-base sm:text-lg text-black/70 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Experience elegant intelligence. Book a demo today.
            </p>
            <Button
              size="lg"
              onClick={() => setDemoOpen(true)}
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105"
            >
              Book a demo
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
};

export default SubPageTemplate;
